/**
 * Contrôles d'un modèle de contrat AVANT publication (éditeur « Modèles de contrat »).
 *
 *   • `{{marques}}` inconnues → le rendu ÉCHOUERAIT (jamais de contrat à trou) : publication bloquée ;
 *   • accolades mal formées (« {{ nom », « {x}} ») → elles ne seraient jamais remplacées : bloquée aussi ;
 *   • passages « [ … ] » restés → des trous à compléter : avertissement, confirmation demandée.
 *
 * Pur : s'appuie sur les fonctions du module de rendu des edge functions (une seule définition de ce qu'est
 * une marque).
 */
import {
  accoladesSuspectes,
  construireVariables,
  crochetsRestants,
  marquesDuModele,
  rendreModele,
  VARIABLES_CONNUES,
  type IdentiteContrat,
  type SujetContrat,
} from '../../../../supabase/functions/_shared/contrat-variables.ts'

export interface ControleModele {
  /** Marques `{{x}}` du dictionnaire présentes dans le texte. */
  connues: string[]
  inconnues: string[]
  accolades: string[]
  /** `[…]`, `[Raison sociale]`… (sans doublon, dans l'ordre d'apparition). */
  crochets: string[]
  /** Rien ne désigne la partie : le contrat serait publié sans {{designation_partie}}. */
  sansDesignation: boolean
  /** Publication impossible tant que ce n'est pas corrigé. */
  bloquant: boolean
}

export function controlerModele(corps: string): ControleModele {
  const { connues, inconnues } = marquesDuModele(corps)
  const accolades = accoladesSuspectes(corps)
  const crochets = [...new Set(crochetsRestants(corps))]
  return {
    connues,
    inconnues,
    accolades,
    crochets,
    sansDesignation: !connues.includes('designation_partie'),
    bloquant: inconnues.length > 0 || accolades.length > 0,
  }
}

/** Les marques que l'éditeur propose à l'insertion, avec leur sens. */
export const MARQUES_PROPOSEES: ReadonlyArray<{ marque: string; sens: string }> = [
  { marque: 'designation_partie', sens: "paragraphe qui désigne la partie selon son statut juridique" },
  { marque: 'nom_partie', sens: 'nom de la partie, pour le bloc de signature' },
  { marque: 'date_du_jour', sens: 'date du jour, en toutes lettres' },
]

/** Identités d'EXEMPLE pour l'aperçu d'un modèle (rien de réel : fictives, jamais envoyées). */
export const IDENTITES_EXEMPLE: Record<'societe' | 'entreprenant' | 'personne_physique', IdentiteContrat> = {
  societe: {
    statut_juridique: 'societe', forme_juridique: 'SARL', raison_sociale: 'Awa Traiteur', capital_fcfa: 1000000,
    rccm: 'ML-BKO-2024-B-01234', nif: '084123456L', siege_adresse: 'à Hamdallaye ACI 2000, Commune IV, Bamako',
    representant_nom: 'Awa Traoré', representant_qualite: 'gérante',
  },
  entreprenant: {
    statut_juridique: 'entreprenant', personne_nom: 'Keita', personne_prenoms: 'Mamadou', nom_commercial: 'Chez Mamadou',
    numero_declaration_entreprenant: 'ML-BKO-ENT-2025-0042', nif: '084999999Z', adresse_personne: 'à Badalabougou, Commune V, Bamako',
    date_naissance: '1990-03-12', lieu_naissance: 'Ségou', type_piece: 'cni', numero_piece: 'CNI-000000',
  },
  personne_physique: {
    statut_juridique: 'personne_physique', personne_nom: 'Coulibaly', personne_prenoms: 'Fatoumata', date_naissance: '1990-03-12',
    lieu_naissance: 'Ségou', type_piece: 'carte_nina', numero_piece: 'NINA 1234567', adresse_personne: 'à Sogoniko, Commune VI, Bamako',
  },
}

/** Le modèle rendu avec une identité d'exemple ; `null` si une marque inconnue empêche le rendu. */
export function apercuModele(
  corps: string,
  sujet: SujetContrat,
  statut: keyof typeof IDENTITES_EXEMPLE,
  maintenant: Date = new Date(),
): { texte: string | null; erreur: string | null } {
  try {
    const v = construireVariables(sujet, IDENTITES_EXEMPLE[statut], maintenant)
    return { texte: rendreModele(corps, v), erreur: null }
  } catch (e) {
    return { texte: null, erreur: e instanceof Error ? e.message : 'Rendu impossible.' }
  }
}

export const NOMS_MARQUES: readonly string[] = VARIABLES_CONNUES
